import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { EntitlementService } from '../../services/entitlement.service';
import {
  PatientSessionModel,
  PatientSessionService,
} from '../../services/patient-session.service';
import { UserService } from '../../services/user';
import { UserAudioService } from '../../services/user-audio.service';
import { isHomeRoute } from '../../utils/session-time.util';

const DAY_COMPLETED_DISMISSED_KEY = 'day-completed-dismissed';
const FREE_PRACTICE_DISMISSED_KEY = 'free-practice-dismissed';

@Component({
  selector: 'app-day-completed',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './day-completed.html',
  styleUrl: './day-completed.css',
})
export class DayCompletedComponent {
  private sessionService = inject(PatientSessionService);
  private userAudioService = inject(UserAudioService);
  private userService = inject(UserService);
  private entitlementService = inject(EntitlementService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);

  private hasCheckedInitialState = signal(false);
  private hasRequestedEntitlements = signal(false);
  private shouldShowFreePractice = signal(false);

  constructor() {
    // When day just completed signal is triggered, show celebration
    effect(() => {
      if (this.sessionService.dayJustCompleted()) {
        // Reset dismissed state for today when day just completed
        this.setDismissed(false);
        // Don't show free practice if day just completed
        this.shouldShowFreePractice.set(false);
      }
    });

    // Check initial state on load - if day is already completed, show free practice
    effect(() => {
      const session = this.sessionService.session();
      const user = this.userService.currentUser();
      const entitlements = this.entitlementService.entitlements();

      if (!session || this.hasCheckedInitialState()) {
        return;
      }

      if (!user) {
        return;
      }

      const needsEntitlementCheck =
        this.userService.isB2CSelfSignup() || this.userService.isPatient();

      if (needsEntitlementCheck && entitlements === null) {
        if (!this.hasRequestedEntitlements()) {
          this.hasRequestedEntitlements.set(true);
          this.entitlementService
            .refreshEntitlements()
            .pipe(take(1))
            .subscribe();
        }
        return;
      }

      if (this.shouldShowFreePracticeUi(session)) {
        this.shouldShowFreePractice.set(true);
      }

      this.handleAudioPlayback(session);
      this.hasCheckedInitialState.set(true);
    });
  }

  private hasFreePracticeTimeRemaining(session: PatientSessionModel): boolean {
    return (session.remainingRequiredExercisesTime ?? 0) > 0;
  }

  private shouldShowCelebrationUi(session: PatientSessionModel): boolean {
    return (
      this.userService.isPatient() &&
      session.isDayCompleted &&
      this.sessionService.dayJustCompleted() &&
      !this.isDismissed()
    );
  }

  private shouldShowFreePracticeUi(session: PatientSessionModel): boolean {
    return (
      this.userService.isPatient() &&
      session.isDayCompleted &&
      this.hasFreePracticeTimeRemaining(session) &&
      !this.sessionService.dayJustCompleted() &&
      !this.isFreePracticeDismissed()
    );
  }

  private handleAudioPlayback(session: PatientSessionModel) {
    if (!session.isDayCompleted) return;

    if (session.isTotalTimeLimitReached) return;

    if (!isHomeRoute(this.router.url)) return;

    if (this.entitlementService.shouldBlockAppUsage()) return;

    if (this.shouldShowCelebrationUi(session)) {
      if (this.hasFreePracticeTimeRemaining(session)) {
        this.userAudioService.playQueued(
          this.locale.toLowerCase().startsWith('de')
            ? 'audio/de/day-completed/spiel-done-und-weiter-im-freien-spiel.ogg'
            : 'audio/hr/day-completed/day_completed_and_free_play.wav',
          false,
          true,
        );
      } else {
        this.userAudioService.playQueued(
          this.locale.toLowerCase().startsWith('de')
            ? 'audio/de/day-completed/aufgaben-in-zeit-und-final-geschafft.ogg'
            : 'audio/hr/day-completed/day_completed_and_finished.wav',
          false,
          true,
        );
      }
      return;
    }

    if (!this.shouldShowFreePracticeUi(session)) return;

    this.userAudioService.playQueued(
      this.locale.toLowerCase().startsWith('de')
        ? 'audio/de/day-completed/spiel-done-und-weiter-im-freien-spiel.ogg'
        : 'audio/hr/day-completed/day_completed_and_free_play.wav',
      false,
      true,
    );
  }

  private isDismissed(): boolean {
    return sessionStorage.getItem(DAY_COMPLETED_DISMISSED_KEY) === 'true';
  }

  private isFreePracticeDismissed(): boolean {
    if (sessionStorage.getItem(FREE_PRACTICE_DISMISSED_KEY) === 'true') {
      return true;
    }
    return false;
  }

  private setDismissed(value: boolean): void {
    if (value) {
      sessionStorage.setItem(DAY_COMPLETED_DISMISSED_KEY, 'true');
    } else {
      sessionStorage.removeItem(DAY_COMPLETED_DISMISSED_KEY);
    }
  }

  private setFreePracticeDismissed(dismissed: boolean): void {
    if (dismissed) {
      sessionStorage.setItem(FREE_PRACTICE_DISMISSED_KEY, 'true');
    } else {
      sessionStorage.removeItem(FREE_PRACTICE_DISMISSED_KEY);
    }
  }

  showCelebrationOverlay = computed(() => {
    const session = this.sessionService.session();
    if (!session) return false;

    return this.shouldShowCelebrationUi(session);
  });

  showFreePracticeMessage = computed(() => {
    const session = this.sessionService.session();
    if (!session) return false;

    return (
      this.shouldShowFreePracticeUi(session) && this.shouldShowFreePractice()
    );
  });

  dismiss() {
    if (this.showCelebrationOverlay()) {
      this.setDismissed(true);
      this.sessionService.dayJustCompleted.set(false);
      this.userAudioService.stopAudioPlayback();
    } else if (this.showFreePracticeMessage()) {
      this.setFreePracticeDismissed(true);
      this.shouldShowFreePractice.set(false);
      this.userAudioService.stopAudioPlayback();
    }
  }
}
